import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import { Loader2, Search, ArrowRight, AlertCircle, ShieldCheck } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { api } from "../../lib/api";
import { validate, splitList } from "../../lib/validate";
import { Field, inputProps, OptionList, ExampleBadge } from "../site/Primitives";
import { preferExpert } from "../site/DiagnosticForm";
import { VerifiedBadge, expertVerifiedSince } from "../site/VerifiedBadge";

const MatchCard = ({ r, i }) => {
  const { t } = useLang();
  const m = t.pro.matching;
  const e = r.expert;
  return (
    <div className="match-result" style={{ animationDelay: `${i * 0.07}s` }} data-testid={`match-result-${i}`}>
      <div className="match-score">
        <span data-testid={`match-score-${i}`}>{r.score}</span><small>/ 100</small>
        <div className="match-bar"><i style={{ width: `${r.score}%` }} /></div>
      </div>
      <div className="match-info">
        <b data-testid={`match-name-${i}`}>{e.name}</b>{e.is_example && <ExampleBadge />}<VerifiedBadge since={expertVerifiedSince(e)} testid={`match-verified-badge-${i}`} />
        <div className="match-meta">
          {e.verticals.map((v) => t.verticalsShort[v]).join(" / ")} · {e.country} · {e.hourly_rate != null
            ? <span data-testid={`match-rate-${i}`}>{e.hourly_rate} {t.common.perHour}</span>
            : <Link to="/espace" className="link-btn" data-testid={`match-login-rate-${i}`}>{t.matchingX.loginForRate}</Link>} · {m.avail} {t.availability[e.availability]}
        </div>
        <div className="match-meta">
          {e.skills.join(", ")}
          {r.matched_skills.length > 0 && <> — <span className="hl">{m.matched} : {r.matched_skills.join(", ")}</span></>}
        </div>
        <div className="breakdown">
          {Object.entries(r.breakdown).map(([k, v]) => <span key={k}>{m.breakdown[k]} {v}</span>)}
        </div>
      </div>
      <div className="match-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => preferExpert(e)} data-testid={`match-request-btn-${i}`}>
          {t.matchingX.request} <ArrowRight className="arrow" />
        </button>
      </div>
    </div>
  );
};

export const Matching = () => {
  const { t } = useLang();
  const m = t.pro.matching;
  const [values, setValues] = useState({ vertical: "", urgency: "blocking", skills: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ loading: false, data: null, failed: false });
  const { user } = useAuth();
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  useEffect(() => { setVerifiedOnly(user?.role === "client"); }, [user]);

  const set = (k) => (e) => { setValues((v) => ({ ...v, [k]: e.target.value })); setErrors((er) => ({ ...er, [k]: undefined })); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values, { vertical: ["required"] });
    setErrors(errs);
    if (errs.vertical) return document.getElementById("match-vertical")?.focus();
    run(verifiedOnly);
  };

  const run = async (verified_only) => {
    setState({ loading: true, data: null, failed: false });
    try {
      const { data } = await api.post("/match", { ...values, skills: splitList(values.skills), verified_only });
      setState({ loading: false, data, failed: false });
    } catch {
      setState({ loading: false, data: null, failed: true });
    }
  };

  return (
    <div className="panel pro-panel" data-testid="matching-panel">
      <p className="pro-intro">{m.intro}</p>
      <form onSubmit={submit} noValidate data-testid="matching-form">
        <div className="f-grid">
          <Field id="match-vertical" label={m.vertical} error={errors.vertical} required>
            <select {...inputProps("match-vertical", errors.vertical, true)} value={values.vertical} onChange={set("vertical")}><OptionList map={t.verticals} withEmpty /></select>
          </Field>
          <Field id="match-urgency" label={m.urgency}>
            <select {...inputProps("match-urgency")} value={values.urgency} onChange={set("urgency")}><OptionList map={t.urgencies} /></select>
          </Field>
          <Field id="match-skills" label={m.skills} span2>
            <input type="text" placeholder={m.skillsPh} {...inputProps("match-skills")} value={values.skills} onChange={set("skills")} />
          </Field>
        </div>
        <div className="check-row verified-toggle">
          <button type="button" role="switch" className="toggle" aria-checked={verifiedOnly} aria-labelledby="verified-only-label"
            onClick={() => { const v = !verifiedOnly; setVerifiedOnly(v); if (state.data) run(v); }} data-testid="matching-verified-only-toggle" />
          <span id="verified-only-label"><ShieldCheck aria-hidden="true" />{t.matchFilter.label}</span>
        </div>
        <div className="f-actions">
          <span className="f-note">{m.note}</span>
          <button type="submit" className="btn btn-primary" disabled={state.loading} data-testid="matching-submit-btn">
            {state.loading ? <Loader2 className="spin" aria-hidden="true" /> : <Search aria-hidden="true" />}{state.loading ? m.searching : m.submit}
          </button>
        </div>
      </form>
      {state.failed && <p className="error-banner" role="alert"><AlertCircle aria-hidden="true" />{t.errors.generic}</p>}
      {state.data && (
        <div aria-live="polite" data-testid="matching-results">
          <p className="match-count" data-testid="matching-count">{m.count(state.data.results.length, state.data.total_pool)}
            {state.data.hidden_unverified > 0 && <> · <span data-testid="matching-hidden-count">{t.matchFilter.hidden(state.data.hidden_unverified)}</span>{" "}
              <button type="button" className="link-btn" onClick={() => { setVerifiedOnly(false); run(false); }} data-testid="matching-show-unverified-btn">{t.matchFilter.show}</button></>}
          </p>
          {state.data.results.length === 0 ? <p className="empty">{m.none}</p> : (
            <div className="match-list">{state.data.results.map((r, i) => <MatchCard key={r.expert.id} r={r} i={i} />)}</div>
          )}
        </div>
      )}
    </div>
  );
};
